import { useState, useEffect, useRef } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { Bot, Send, Loader2, Plus, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STARTERS = [
    'What exactly is a CRT range?',
    'When are the kill zones I should trade?',
    'How do I align timeframes top-down?',
    'Explain turtle soup with an example',
];

function Message({ role, content }) {
    const isUser = role === 'user';
    return (
        <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
            <div
                className="max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap"
                style={isUser
                    ? { backgroundColor: 'var(--primary-color, #00FFA3)', color: '#06110C', borderBottomRightRadius: 6 }
                    : { backgroundColor: 'var(--background-elevated, #101418)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text-primary, #E7EDF3)', borderBottomLeftRadius: 6 }}
                data-testid={isUser ? 'assistant-message-user' : 'assistant-message-ai'}
            >
                {content}
            </div>
        </div>
    );
}

export default function Assistant() {
    const [sessions, setSessions] = useState([]);
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [quota, setQuota] = useState(null);
    const bottomRef = useRef(null);

    const refreshQuota = () => {
        axios.get(`${API}/assistant/quota`, { withCredentials: true })
            .then((res) => setQuota(res.data))
            .catch(() => {});
    };

    useEffect(() => {
        axios.get(`${API}/assistant/sessions`, { withCredentials: true })
            .then((res) => setSessions(res.data))
            .catch(() => {});
        refreshQuota();
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, sending]);

    const loadSession = async (sid) => {
        try {
            const res = await axios.get(`${API}/assistant/chat/${sid}`, { withCredentials: true });
            setSessionId(sid);
            setMessages(res.data.messages);
        } catch (e) {
            toast.error('Could not load that conversation');
        }
    };

    const newChat = () => {
        setSessionId(null);
        setMessages([]);
    };

    const send = async (text) => {
        const message = (text ?? input).trim();
        if (!message || sending) return;
        setInput('');
        setMessages((m) => [...m, { role: 'user', content: message }]);
        setSending(true);
        try {
            const res = await axios.post(`${API}/assistant/chat`, { message, session_id: sessionId }, { withCredentials: true });
            const { job_id, session_id: sid } = res.data;
            let reply = null;
            for (let i = 0; i < 70; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data } = await axios.get(`${API}/assistant/job/${job_id}`, { withCredentials: true });
                if (data.status === 'done') { reply = data.reply; break; }
                if (data.status === 'error') throw new Error(data.error || 'The assistant could not reply — try again');
            }
            if (!reply) throw new Error('The assistant took too long — please try again');
            setMessages((m) => [...m, { role: 'assistant', content: reply }]);
            refreshQuota();
            if (!sessionId) {
                setSessionId(sid);
                setSessions((s) => [{ session_id: sid, title: message.slice(0, 60) }, ...s]);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || 'The assistant could not reply — try again');
            setMessages((m) => m.slice(0, -1));
            setInput(message);
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--background-base, #07090B)' }}>
            <Header />
            <main className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 py-6 flex flex-col" data-testid="assistant-page">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2.5">
                            <Bot className="w-6 h-6" style={{ color: 'var(--primary-color, #00FFA3)' }} />
                            CRT Assistant
                        </h1>
                        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                            Trained on the full NIKAYOFX PTG CRT video course — ask anything about the strategy
                        </p>
                        {quota && (
                            <p className="text-xs mt-1 font-medium" style={{ color: quota.remaining > 0 ? 'var(--text-muted)' : '#D9534F' }} data-testid="assistant-quota">
                                {quota.remaining > 0
                                    ? `${quota.remaining} of ${quota.limit} questions left today`
                                    : 'Daily question limit reached — resets at midnight (UTC)'}
                            </p>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {sessions.length > 0 && (
                            <Select value={sessionId || ''} onValueChange={loadSession}>
                                <SelectTrigger className="w-44" data-testid="assistant-session-select">
                                    <SelectValue placeholder="Past chats" />
                                </SelectTrigger>
                                <SelectContent>
                                    {sessions.map((s) => (
                                        <SelectItem key={s.session_id} value={s.session_id}>{s.title || 'Chat'}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        <Button variant="outline" size="sm" onClick={newChat} data-testid="assistant-new-chat-btn">
                            <Plus className="w-4 h-4 mr-1" /> New
                        </Button>
                    </div>
                </div>

                <div className="flex-1 space-y-4 overflow-y-auto pb-4" style={{ minHeight: '40vh' }}>
                    {messages.length === 0 && (
                        <div className="text-center py-10">
                            <GraduationCap className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: 'var(--primary-color, #00FFA3)' }} />
                            <p className="text-sm mb-5" style={{ color: 'var(--text-muted)' }}>
                                Ask me anything from the CRT course — concepts, setups, timing, entries…
                            </p>
                            <div className="flex flex-wrap justify-center gap-2">
                                {STARTERS.map((q) => (
                                    <button
                                        key={q}
                                        onClick={() => send(q)}
                                        className="text-xs px-3 py-2 rounded-full transition-all hover:opacity-80"
                                        style={{ backgroundColor: 'rgba(0,255,163,0.07)', border: '1px solid rgba(0,255,163,0.25)', color: 'var(--primary-color, #00FFA3)' }}
                                        data-testid="assistant-starter-chip"
                                    >
                                        {q}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {messages.map((m, i) => (
                        <Message key={i} role={m.role} content={m.content} />
                    ))}
                    {sending && (
                        <div className="flex justify-start">
                            <div className="rounded-2xl px-4 py-3" style={{ backgroundColor: 'var(--background-elevated, #101418)', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="assistant-typing">
                                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--primary-color, #00FFA3)' }} />
                            </div>
                        </div>
                    )}
                    <div ref={bottomRef} />
                </div>

                <form
                    onSubmit={(e) => { e.preventDefault(); send(); }}
                    className="flex gap-2 pt-3 sticky bottom-0 pb-4"
                    style={{ backgroundColor: 'var(--background-base, #07090B)' }}
                >
                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        placeholder={quota && quota.remaining <= 0 ? 'Daily limit reached — come back tomorrow' : 'Ask about the CRT strategy…'}
                        maxLength={2000}
                        disabled={quota ? quota.remaining <= 0 : false}
                        className="flex-1 rounded-lg px-4 py-3 text-sm outline-none disabled:opacity-60"
                        style={{ backgroundColor: 'var(--background-elevated, #101418)', border: '1px solid rgba(255,255,255,0.12)', color: 'var(--text-primary, #E7EDF3)' }}
                        data-testid="assistant-input"
                    />
                    <Button type="submit" disabled={!input.trim() || sending || (quota && quota.remaining <= 0)} className="px-4" data-testid="assistant-send-btn">
                        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                </form>
            </main>
        </div>
    );
}
